/**
 * Authentication context provider — JWT + Azure AD SSO.
 *
 * State sync rules:
 *   - Tokens are stored exclusively via tokenStorage. AuthContext never
 *     touches localStorage directly.
 *   - When the axios interceptor in services/api.ts decides the session
 *     is over (refresh failed, no RT, etc.) it dispatches an
 *     `auth:logout` window event. We listen for it here and clear React
 *     state + redirect — this is what makes a 401 actually log the user
 *     out instead of leaving the UI in a half-authenticated state.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { AUTH_LOGOUT_EVENT } from '../../services/authEvents';
import {
  ACCESS_TOKEN_KEY,
  AZURE_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  tokenStorage,
} from '../../services/tokenStorage';
import { azureAuthService } from '../../services/azureAuthService';
import { User } from '../../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authMethod: 'jwt' | 'azure_sso' | null;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithAzure: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState<'jwt' | 'azure_sso' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = user !== null;

  const detectAuthMethod = useCallback((): 'jwt' | 'azure_sso' | null => {
    if (localStorage.getItem(AZURE_TOKEN_KEY)) return 'azure_sso';
    if (localStorage.getItem(ACCESS_TOKEN_KEY)) return 'jwt';
    return null;
  }, []);

  // -------------------------------------------------------------------
  // Check authentication status. Relies on the axios interceptor to
  // refresh tokens transparently — we only see success/failure here.
  // -------------------------------------------------------------------
  const checkAuth = useCallback(async () => {
    const method = detectAuthMethod();
    if (method === null) {
      setUser(null);
      setAuthMethod(null);
      setIsLoading(false);
      return;
    }

    try {
      const meResp = await api.get<User>('/api/auth/me');
      setUser(meResp.data);
      setAuthMethod(method);
    } catch {
      // Interceptor has already cleared tokens / emitted auth:logout
      // for unrecoverable failures. Defensive cleanup here.
      tokenStorage.clearAll();
      setUser(null);
      setAuthMethod(null);
    } finally {
      setIsLoading(false);
    }
  }, [detectAuthMethod]);

  // -------------------------------------------------------------------
  // Handle Azure callback from URL params
  // -------------------------------------------------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (code) {
      const handleAzureCallback = async () => {
        try {
          setIsLoading(true);
          const response = await azureAuthService.handleAzureCallback(
            code,
            state || undefined,
          );
          tokenStorage.setAccessToken(response.access_token, 'azure_sso');
          if (response.refresh_token) {
            tokenStorage.setRefreshToken(response.refresh_token);
          }

          const userData = await azureAuthService.getCurrentUser();
          setUser(userData);
          setAuthMethod('azure_sso');

          window.history.replaceState({}, document.title, '/login');
          const from = (location.state as any)?.from?.pathname || '/';
          navigate(from, { replace: true });
        } catch (err) {
          console.error('Azure callback error:', err);
        } finally {
          setIsLoading(false);
        }
      };
      handleAzureCallback();
    } else if (error) {
      console.error('Azure SSO error:', error);
    }
  }, [location.search, navigate, location.state]);

  // Initial auth check
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // -------------------------------------------------------------------
  // React to forced logout (interceptor → /refresh failed / no RT).
  // This is what makes 401s actually log the user out of the UI.
  // -------------------------------------------------------------------
  useEffect(() => {
    const handleAuthLogout = () => {
      setUser(null);
      setAuthMethod(null);
      setIsLoading(false);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
  }, [navigate, location.pathname]);

  // Multi-tab sync + visibility re-check
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === ACCESS_TOKEN_KEY ||
        e.key === AZURE_TOKEN_KEY ||
        e.key === REFRESH_TOKEN_KEY
      ) {
        if (e.newValue) {
          checkAuth();
        } else if (!tokenStorage.getAccessToken()) {
          // All tokens cleared in another tab → mirror the logout here.
          setUser(null);
          setAuthMethod(null);
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      }
    };

    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        !isAuthenticated &&
        tokenStorage.getAccessToken()
      ) {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, isAuthenticated, checkAuth, location.pathname]);

  // Redirect to /login if unauthenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname !== '/login') {
      navigate('/login', { state: { from: location } });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate, location]);

  // -------------------------------------------------------------------
  // Login (local JWT)
  // -------------------------------------------------------------------
  const login = useCallback(async (loginVal: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/api/auth/login', {
        login: loginVal,
        password,
      });

      const { access_token, refresh_token } = response.data ?? {};
      if (!access_token) {
        return false;
      }
      tokenStorage.setAccessToken(access_token, 'jwt');
      if (refresh_token) {
        tokenStorage.setRefreshToken(refresh_token);
      }

      const meResp = await api.get<User>('/api/auth/me');
      setUser(meResp.data);
      setAuthMethod('jwt');
      setIsLoading(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  // -------------------------------------------------------------------
  // Azure SSO login
  // -------------------------------------------------------------------
  const loginWithAzure = useCallback(async () => {
    try {
      await azureAuthService.initiateAzureLogin();
    } catch (error) {
      console.error('Azure login error:', error);
      throw error;
    }
  }, []);

  // -------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------
  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      await api.post('/api/auth/logout', refreshToken ? { refresh_token: refreshToken } : {});
    } catch (error) {
      // Best-effort: even if the network call fails, we proceed with
      // local cleanup so the user lands on /login.
      console.error('Logout error:', error);
    } finally {
      tokenStorage.clearAll();
      setUser(null);
      setAuthMethod(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    authMethod,
    login,
    loginWithAzure,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
